import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useFamily } from '../../contexts/FamilyContext';
import { axiosInstance } from '../../services/api';
import { toast } from 'react-toastify';

/**
 * Page for logged-in users to create a new family or join one via share link.
 * Accessible from FamilySwitcher's "Create or Join a Family" button.
 */
function ManageFamilies() {
  const navigate = useNavigate();
  const { families, updateFamilies, primaryFamilyId, switchFamily } = useFamily();
  const [mode, setMode] = useState(null); // null | 'create' | 'join'
  const [familyName, setFamilyName] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleCreateFamily = async (e) => {
    e.preventDefault();
    if (!familyName.trim()) return;

    try {
      setSubmitting(true);
      const response = await axiosInstance.post('/account/families/create/', {
        name: familyName.trim(),
      });

      if (response.data.success) {
        const newFamily = response.data.family;
        const updatedFamilies = [
          ...families,
          {
            id: newFamily.id,
            name: newFamily.name,
            role: 'admin',
            shareable_link: newFamily.shareable_link,
            member_count: 1,
          },
        ];
        updateFamilies(updatedFamilies, primaryFamilyId);
        await switchFamily(newFamily.id);
        toast.success(`"${newFamily.name}" created! You can now invite family members.`);
        navigate('/family-hub/invite');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create family';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinFamily = async (e) => {
    e.preventDefault();
    if (!shareLink.trim()) return;

    // Extract token from share link (could be full URL or just the ID)
    let shareToken = shareLink.trim();
    // Handle full URLs like https://app.1g2n.com/join/abc-123
    const urlMatch = shareToken.match(/\/join\/([a-f0-9-]+)/i);
    if (urlMatch) {
      shareToken = urlMatch[1];
    }
    // Handle invitation URLs like /join/invitation/{id}/{token}
    const invMatch = shareToken.match(/\/join\/invitation\/([^/]+)\/([^/]+)/);
    if (invMatch) {
      // This is an invitation link, redirect to AcceptInvitation
      navigate(`/join/invitation/${invMatch[1]}/${invMatch[2]}`);
      return;
    }

    try {
      setSubmitting(true);
      const response = await axiosInstance.post('/account/join-additional-family/', {
        share_token: shareToken,
      });

      if (response.data.success) {
        const newFamily = response.data.family;
        const updatedFamilies = [
          ...families,
          {
            id: newFamily.id,
            name: newFamily.name,
            role: 'member',
            member_count: null,
          },
        ];
        updateFamilies(updatedFamilies, primaryFamilyId);
        await switchFamily(newFamily.id);
        toast.success(`Welcome to ${newFamily.name}!`);
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to join family. Check the link and try again.';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-lg mx-auto py-8 px-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#556B2F] font-medium mb-6 hover:underline"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
          </svg>
          Back
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Create or Join a Family</h1>
        <p className="text-gray-500 mb-8">
          You're currently in {families.length} {families.length === 1 ? 'family' : 'families'}. Add another below.
        </p>

        {/* Option cards (when no mode selected) */}
        {!mode && (
          <div className="space-y-4">
            <button
              onClick={() => setMode('create')}
              className="w-full bg-white rounded-xl border-2 border-gray-200 hover:border-[#6B8E23] p-6 text-left transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F0F4E8] flex items-center justify-center flex-shrink-0">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#556B2F" strokeWidth="2" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">Create a New Family</div>
                  <div className="text-sm text-gray-500 mt-0.5">Start a new family group and invite members</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => setMode('join')}
              className="w-full bg-white rounded-xl border-2 border-gray-200 hover:border-[#6B8E23] p-6 text-left transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F0F4E8] flex items-center justify-center flex-shrink-0">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#556B2F" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">Join an Existing Family</div>
                  <div className="text-sm text-gray-500 mt-0.5">Use an invite link shared by a family member</div>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Create family form */}
        {mode === 'create' && (
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Create a New Family</h2>
            <p className="text-sm text-gray-500 mb-5">Give your family a name to get started</p>

            <form onSubmit={handleCreateFamily}>
              <label className="block text-sm font-bold text-gray-700 mb-2">Family Name</label>
              <input
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="e.g. Sharma Family"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:border-[#556B2F] focus:outline-none mb-4"
                autoFocus
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setMode(null)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!familyName.trim() || submitting}
                  className="flex-1 py-3 rounded-xl bg-[#556B2F] text-white font-bold hover:bg-[#4A5D23] transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Family'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Join family form */}
        {mode === 'join' && (
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Join an Existing Family</h2>
            <p className="text-sm text-gray-500 mb-5">Paste the invite link you received from a family member</p>

            <form onSubmit={handleJoinFamily}>
              <label className="block text-sm font-bold text-gray-700 mb-2">Invite Link</label>
              <input
                type="text"
                value={shareLink}
                onChange={(e) => setShareLink(e.target.value)}
                placeholder="Paste invite link here..."
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:border-[#556B2F] focus:outline-none mb-4"
                autoFocus
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setMode(null)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!shareLink.trim() || submitting}
                  className="flex-1 py-3 rounded-xl bg-[#556B2F] text-white font-bold hover:bg-[#4A5D23] transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Joining...
                    </>
                  ) : (
                    'Join Family'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ManageFamilies;
